/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import Moment from "moment"; // Npm: Moment date formatting.
import _ from "underscore"; // Npm: Utility module.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { useHistory } from "react-router-dom"; // Npm: React router dom.
import { IoMdAdd } from "react-icons/io"; // Npm: React icons.
import { HiArrowPath } from "react-icons/hi2"; // Npm: React icons.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI DataGrid.
import {
  Box,
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import Modal from "components/Modal";
import PduScriptDelete from "components/PduScriptDelete";
import PduScriptDropDownOptions from "components/PduScriptDropDownOptions";
import TableSpinner from "components/TableSpinner";

/*
 * GRAPHS
 */
import ScriptReadQuery from "./__query__/index.script.read.query";

/*
 * STYLES
 */
import { buttonStyle } from "./index.style";

/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags = React.useMemo(
    () =>
      Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
        (i) => i.key,
      ),
    [],
  );

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const _rowCountRef = React.useRef(0);
  const _tableHeaderHeightRef = React.useRef(0);
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _historyRef = useHistory();
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  const _isCurrentViewMobile = useBreakpointValue(
    { base: true, sm: true, md: false, lg: false, xl: false },
    { fallback: "base" },
  );

  const {
    isOpen: isScriptDeleteOpen,
    onOpen: onScriptDeleteOpen,
    onClose: onScriptDeleteClose,
  } = useDisclosure();
  const _isFilterApplied = React.useMemo(
    () =>
      filterModel.items.some(
        (item) => item.value !== void 0 && item.value !== "",
      ),
    [filterModel.items],
  );
  const _scriptReadVariables = React.useMemo(
    () => ({
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
      filter: _isFilterApplied ? filterModel : void 0,
    }),
    [
      _isFilterApplied,
      filterModel,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const _QueryScriptRead = useQuery(ScriptReadQuery, {
    variables: _scriptReadVariables,
    fetchPolicy: _isPreviousPageRef.current
      ? "cache-first"
      : Object.React.App.fetchPolicy,
  });

  React.useEffect(
    () => () => {
      _setFilterDebounced.cancel();
    },
    [_setFilterDebounced],
  );

  // Event handler.
  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false;
    setPaginationModel((current) => {
      const _next = { ...current, page: 0 };

      _paginationModelRef.current = _next;

      return _next;
    });
  }, []);
  const _HandlePaginationModelChange = React.useCallback(
    (__paginationModel) => {
      const _nextPaginationModel =
        __paginationModel.pageSize !== _paginationModelRef.current.pageSize
          ? { ...__paginationModel, page: 0 }
          : __paginationModel;

      _isPreviousPageRef.current =
        _nextPaginationModel.page < _paginationModelRef.current.page &&
        _nextPaginationModel.pageSize === _paginationModelRef.current.pageSize;
      _paginationModelRef.current = _nextPaginationModel;
      setPaginationModel(_nextPaginationModel);
    },
    [],
  );
  const _HandleFilterModelChange = React.useCallback(
    (__filterModel) => {
      _ResetPagination();
      _setFilterDebounced(__filterModel);
    },
    [_ResetPagination, _setFilterDebounced],
  );
  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _QueryScriptRead.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_QueryScriptRead.refetch]);

  // Columns definition.
  const columns = React.useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
      },
      {
        field: "name",
        headerName: "Script Name",
        flex: 1,
        minWidth: 180,
        align: "center",
        headerAlign: "center",
        filterable: true,
      },
      {
        field: "scriptCode",
        headerName: "Script Code",
        flex: 1,
        minWidth: 220,
        align: "center",
        headerAlign: "center",
        filterable: true,
      },

      {
        field: "createdAt",
        headerName: "Created At",
        flex: 1,
        minWidth: 130,
        align: "center",
        headerAlign: "center",
        filterable: false,
      },
      {
        field: "action",
        headerName: "Action",
        width: 100,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <PduScriptDropDownOptions
            scriptId={row.scriptId}
            scriptName={row.name}
            onScriptDelete={onScriptDeleteOpen}
          />
        ),
      },
    ],
    [onScriptDeleteOpen],
  );
  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryScriptRead.data?.ScriptRead?.map((item, index) =>
          _successFlags.includes(item?.status)
            ? {
                id: item?.id ?? `pdu-script-${paginationModel.page}-${index}`,
                scriptId: item?.id,
                name: item?.name || "-",
                scriptCode: item?.scriptCode || "-",
                createdAt: item?.createdAt
                  ? Moment(item.createdAt).format("YYYY-MM-DD")
                  : "-",
              }
            : null,
        ),
      ),
    [_QueryScriptRead.data?.ScriptRead, _successFlags, paginationModel.page],
  );
  const _rowCount =
    _.first(_QueryScriptRead.data?.ScriptRead)?._totalCount ??
    _QueryScriptRead.data?.ScriptRead?.length;
  const rowCount = React.useMemo(() => {
    if (_rowCount !== void 0 && _rowCount !== null)
      _rowCountRef.current = _rowCount;

    return _rowCountRef.current;
  }, [_rowCount]);
  const slots = React.useMemo(
    () => ({
      loadingOverlay: () => (
        <TableSpinner
          isLoading={true}
          isEmpty={false}
          chopHeightFromHundredPercentage={
            _tableHeaderHeightRef.current?.clientHeight
          }
        />
      ),
      noResultsOverlay: () => (
        <TableSpinner
          isLoading={false}
          isEmpty={true}
          chopHeightFromHundredPercentage={
            _tableHeaderHeightRef.current?.clientHeight
          }
        />
      ),
      noRowsOverlay: () => (
        <TableSpinner
          isLoading={false}
          isEmpty={true}
          chopHeightFromHundredPercentage={
            _tableHeaderHeightRef.current?.clientHeight
          }
        />
      ),
    }),
    [],
  );

  // Return component.
  return (
    <Flex
      className="pduScript base"
      height="100vh"
      overflow="visible"
    >
      <Flex
        display="flex"
        flex={1}
        flexDirection="column"
        gap={_isCurrentViewMobile ? "12px" : "14px"}
        bg="white"
        borderRadius="12px"
        minH="calc(100vh - 300px)"
        p={_isCurrentViewMobile ? "12px" : "22px"}
      >
        <Flex
          pb="0px"
          justify="space-between"
          overflow="hidden"
          flexWrap={{ base: "wrap", md: "wrap" }}
          gap="12px"
        >
          <Tooltip label="Create New PDU Script" fontSize="sm">
            <Button
              onClick={() => _historyRef.push("/pdu-script/create")}
              leftIcon={<IoMdAdd />}
              style={buttonStyle}
            >
              Create Script
            </Button>
          </Tooltip>
          <Tooltip label="Refresh PDU scripts" fontSize="sm">
            <Button
              borderRadius="8px"
              fontWeight="450"
              minW="20px"
              backgroundColor="purple.200"
              color="purple"
              _hover={{ bg: "purple.800", color: "white" }}
              leftIcon={<HiArrowPath />}
              isDisabled={isRefreshing}
              onClick={_HandleRefresh}
            >
              Refresh
            </Button>
          </Tooltip>
        </Flex>

        <Box
          w="100%"
          height="calc(100vh - 240px)"
          flexShrink={0}
          overflow="hidden"
        >
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={50}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            pageSizeOptions={[10, 30, 50, 100]}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            slots={slots}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            loading={_QueryScriptRead.loading}
          />
        </Box>
      </Flex>

      <Modal
        size="md"
        title="PDU Script Delete"
        isOpen={isScriptDeleteOpen}
        onClose={onScriptDeleteClose}
      >
        <PduScriptDelete
          onClose={onScriptDeleteClose}
          refetch={_QueryScriptRead.refetch}
        />
      </Modal>
    </Flex>
  );
};

/*
 * EXPORT
 */
export default Index;
